import { ColumnType } from '../types';
import { TableViewState } from './types';
import {
  isGroup, isSystem, partitionSystem, reattachSystem, stateKeyOf,
} from './columns';
import { applyVisibility } from './visibility';
import { mergeLevelOrder } from './order';
import { applyPinning } from './pinning';
import { warnColumnInvariants, warnNonPinnablePins, warnStaleStateKeys } from './invariants';

/**
 * Resolve declarative columns + persisted user state into antd-ready columns.
 *
 * Pipeline: visibility (drop hidden leaves and empty groups) → order (top-level
 * then per-group children, config-neighbor insertion for new/anchored keys) →
 * pinning (render model C: `fixed: 'left'` on pinned leaves, per-group prefix
 * normalization). System columns — including system groups — bypass
 * visibility/order/pin and re-attach at their original indices. Absent state
 * means pure column-meta defaults.
 *
 * Meta cascade: `configurable`/`defaultEnabled`/`defaultPinned`/`pinnable` set
 * on a group provide the default for its children; explicit child values win.
 *
 * Nesting: settings resolution is two levels deep. A sub-group nested at
 * depth ≥ 3 is treated as an atomic unit keyed by `settingsKey ?? key` — it is
 * shown/hidden, pinned, and ordered as one thing, and its inner structure is
 * kept as authored (dev warning via invariants; see "Column nesting depth" in
 * `./table-view-state-versioning.md`).
 *
 * Pure and deterministic — no React, no mutation of the input columns or state.
 */
export function applyTableViewState<R>(
  columns: ColumnType<R>[],
  state?: TableViewState | null,
): ColumnType<R>[] {
  warnColumnInvariants(columns);
  warnNonPinnablePins(columns, state);
  warnStaleStateKeys(columns, state);

  const visible = applyVisibility(columns, state);
  const topLevelOrdered = applyTopLevelOrder(visible, state);
  const childOrdered = applyChildOrder(topLevelOrdered, state);
  return applyPinning(childOrdered, state);
}

function applyTopLevelOrder<R>(
  columns: ColumnType<R>[],
  state: TableViewState | null | undefined,
): ColumnType<R>[] {
  const { movable, system } = partitionSystem(columns);
  const ordered = mergeLevelOrder(movable, state?.order?.topLevel);
  return reattachSystem(ordered, system);
}

function applyChildOrder<R>(
  columns: ColumnType<R>[],
  state: TableViewState | null | undefined,
): ColumnType<R>[] {
  const childrenOrder = state?.order?.children;
  return columns.map((col) => {
    if (!isGroup(col) || isSystem(col)) return col;
    const { movable, system } = partitionSystem(col.children ?? []);
    const ordered = mergeLevelOrder(movable, childrenOrder?.[stateKeyOf(col)]);
    return { ...col, children: reattachSystem(ordered, system) };
  });
}
